import React, { useEffect, useState, useRef } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { Card } from "@/components/ui/card";
import { Table, TableHeader, TableHead, TableRow, TableBody, TableCell } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataToolbar, StatusBadge } from "@/components/data/Common";
import { api, API, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Download, FileText, Trash2, UploadCloud } from "lucide-react";

const CATEGORIES = ["Drawing", "OEM Manual", "SOP", "Contract", "Inspection Report", "Calibration Certificate", "Warranty", "Photograph", "Other"];
const LINK_TYPES = [
  ["general", "General"], ["project", "Project"], ["equipment", "Equipment"],
  ["workorder", "Work Order"], ["breakdown", "Breakdown"], ["pm", "PM Schedule"],
  ["inventory", "Inventory"], ["safety", "HSE"], ["quality", "Quality"],
];

export default function Documents() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [linkFilter, setLinkFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [form, setForm] = useState({ title: "", category: "OEM Manual", linked_type: "general", linked_id: "", version: "1.0" });
  const fileRef = useRef();

  const load = () => api.get("/documents").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!file) return toast.error("Select a file");
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded"); setOpen(false); setFile(null);
      setForm({ title: "", category: "OEM Manual", linked_type: "general", linked_id: "", version: "1.0" });
      load();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const download = async (d) => {
    const t = localStorage.getItem("neipl_token");
    const res = await fetch(`${API}/documents/${d.id}/download`, { headers: { Authorization: `Bearer ${t}` } });
    const blob = await res.blob();
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = u; a.download = d.original_filename; a.click();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try { await api.delete(`/documents/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const filt = list.filter((d) =>
    (catFilter === "all" || d.category === catFilter) &&
    (linkFilter === "all" || d.linked_type === linkFilter) &&
    (!q || `${d.title} ${d.original_filename}`.toLowerCase().includes(q.toLowerCase()))
  );

  const fmtSize = (b) => b < 1024 ? `${b} B` : b < 1048576 ? `${(b/1024).toFixed(1)} KB` : `${(b/1048576).toFixed(1)} MB`;

  return (
    <AppLayout title="Document Management" subtitle="Drawings · Manuals · SOPs · Photographs · Certificates">
      <DataToolbar search={q} setSearch={setQ} onAdd={() => setOpen(true)} addLabel="Upload"
        addTestId="doc-add-button" searchTestId="doc-search-input"
        filters={
          <>
            <Select value={catFilter} onValueChange={setCatFilter}>
              <SelectTrigger className="w-44" data-testid="doc-category-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All categories</SelectItem>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={linkFilter} onValueChange={setLinkFilter}>
              <SelectTrigger className="w-40" data-testid="doc-link-filter"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">All links</SelectItem>{LINK_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </>
        }
      />
      <Card className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead></TableHead>
              <TableHead>Title</TableHead><TableHead>Category</TableHead><TableHead>Linked To</TableHead>
              <TableHead>Version</TableHead><TableHead>File</TableHead>
              <TableHead className="text-right">Size</TableHead><TableHead>Uploaded By</TableHead>
              <TableHead>Date</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filt.map((d) => (
              <TableRow key={d.id} data-testid={`doc-row-${d.id}`}>
                <TableCell><FileText className="h-4 w-4 text-sky-700" /></TableCell>
                <TableCell className="font-medium">{d.title}</TableCell>
                <TableCell><StatusBadge status="active" /> <span className="ml-1 text-xs">{d.category}</span></TableCell>
                <TableCell className="text-xs text-slate-500">{LINK_TYPES.find(([v]) => v === d.linked_type)?.[1] || d.linked_type}</TableCell>
                <TableCell className="font-mono text-xs">v{d.version}</TableCell>
                <TableCell className="text-xs text-slate-500 max-w-[200px] truncate">{d.original_filename}</TableCell>
                <TableCell className="text-right font-mono text-xs">{fmtSize(d.size)}</TableCell>
                <TableCell className="text-xs">{d.uploaded_by_name}</TableCell>
                <TableCell className="font-mono text-xs">{d.created_at?.slice(0, 10)}</TableCell>
                <TableCell className="whitespace-nowrap space-x-1">
                  <button onClick={() => download(d)} className="text-xs px-2 py-1 border rounded hover:bg-sky-50" data-testid={`doc-download-${d.id}`}>
                    <Download className="h-3 w-3 inline" />
                  </button>
                  <button onClick={() => remove(d.id)} className="text-xs px-2 py-1 border rounded hover:bg-red-50 text-red-600" data-testid={`doc-delete-${d.id}`}>
                    <Trash2 className="h-3 w-3 inline" />
                  </button>
                </TableCell>
              </TableRow>
            ))}
            {!filt.length && <TableRow><TableCell colSpan={10} className="text-center py-12 text-slate-400">No documents</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="doc-upload-dialog">
          <DialogHeader><DialogTitle>Upload Document</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="label-cap">File</Label>
              <div onClick={() => fileRef.current?.click()} className="border-2 border-dashed border-slate-300 hover:border-sky-500 rounded-md p-6 text-center cursor-pointer">
                <UploadCloud className="h-6 w-6 mx-auto text-slate-400" />
                <div className="text-sm mt-1">{file ? file.name : "Click to select a file"}</div>
                <div className="text-xs text-slate-400 mt-0.5">PDF, images, drawings — up to 20 MB</div>
              </div>
              <input ref={fileRef} type="file" hidden onChange={(e) => setFile(e.target.files[0])} data-testid="doc-file-input" />
            </div>
            <div><Label className="label-cap">Title</Label><Input data-testid="doc-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-cap">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="doc-category-input"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="label-cap">Version</Label><Input data-testid="doc-version-input" value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} /></div>
              <div>
                <Label className="label-cap">Linked To</Label>
                <Select value={form.linked_type} onValueChange={(v) => setForm({ ...form, linked_type: v })}>
                  <SelectTrigger data-testid="doc-linked-type-input"><SelectValue /></SelectTrigger>
                  <SelectContent>{LINK_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="label-cap">Linked ID (optional)</Label><Input data-testid="doc-linked-id-input" value={form.linked_id} onChange={(e) => setForm({ ...form, linked_id: e.target.value })} placeholder="Equipment / WO / etc." /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={submit} className="bg-sky-900 hover:bg-sky-950" data-testid="doc-submit-button">Upload</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
